import { Component } from '@theme/component';

const SWIPE_THRESHOLD = 40;
const SLIDE_STATUS = 'Slide {{ index }} of {{ count }}';

/**
 * Bo+Tee Hero banner carousel (OHP001P-158).
 * One-slide hosts no-op. Multi-slide: wrap, 6s auto-rotate, pause on
 * hover / focus / touch / hidden tab, reduced-motion skips animation.
 *
 * @extends {Component}
 */
class BtHeroCarousel extends Component {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLElement[]} */
  #dots = [];

  /** @type {HTMLElement | null} */
  #status = null;

  /** @type {HTMLElement | null} */
  #progressFill = null;

  #index = 0;
  #animating = false;
  #autoplaySeconds = 0;
  #reducedMotion = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #timer = null;

  /** @type {Set<string>} */
  #pauseReasons = new Set();

  /** @type {number | null} */
  #pointerId = null;

  #pointerStartX = 0;

  #pointerLastX = 0;

  #didSwipe = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #wrapFallback = null;

  /** @type {(event: Event) => void} */
  #onClick = this.#handleClick.bind(this);

  /** @type {(event: KeyboardEvent) => void} */
  #onKeydown = this.#handleKeydown.bind(this);

  #inView = false;

  /** @type {IntersectionObserver | null} */
  #viewObserver = null;

  /**
   * Arrow keys also drive the hero when nothing on the page has focus and it is mostly on screen.
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (!this.#inView || event.defaultPrevented) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;
    this.#handleKeydown(event);
  };

  /** @type {(event: PointerEvent) => void} */
  #onPointerDown = this.#handlePointerDown.bind(this);

  /** @type {(event: PointerEvent) => void} */
  #onPointerMove = this.#handlePointerMove.bind(this);

  /** @type {(event: PointerEvent) => void} */
  #onPointerUp = this.#handlePointerUp.bind(this);

  /** @type {() => void} */
  #onVisibility = this.#handleVisibility.bind(this);

  /** @type {(event: TransitionEvent) => void} */
  #onTransitionEnd = this.#handleTransitionEnd.bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.#track = this.querySelector('[data-bt-hero-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-bt-hero-slide]')).filter(
      (node) => node instanceof HTMLElement && !node.hasAttribute('data-bt-hero-clone')
    );
    this.#dots = Array.from(this.querySelectorAll('[data-bt-hero-dot]')).filter(
      (node) => node instanceof HTMLElement
    );
    this.#status = this.querySelector('[data-bt-hero-status]');
    this.#progressFill = this.querySelector('[data-bt-hero-progress-fill]');

    if (this.#slides.length < 2 || !this.#track) return;

    const autoplay = Number.parseInt(this.dataset.autoplay || '', 10);
    this.#autoplaySeconds = Number.isFinite(autoplay) && autoplay > 0 ? autoplay : 0;
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#track.addEventListener('transitionend', this.#onTransitionEnd);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
    this.addEventListener('pointerenter', this.#onHoverEnter);
    this.addEventListener('pointerleave', this.#onHoverLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('visibilitychange', this.#onVisibility);
    document.addEventListener('keydown', this.#onDocumentKeydown);

    this.#viewObserver = new IntersectionObserver(
      ([entry]) => {
        this.#inView = !!entry && entry.intersectionRatio >= 0.5;
      },
      { threshold: [0, 0.5, 1] }
    );
    this.#viewObserver.observe(this);

    if (this.#reducedMotion) {
      this.querySelectorAll('video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
    }

    this.#updateChrome();
    this.#syncVideos();
    this.#syncTimer();
    this.#warmSlideImages();
  }

  /**
   * Off-screen slides sit in a clipped track, so native lazy-loading would not fetch them until they
   * slide in. Load them after the page so the first slide's LCP keeps priority.
   */
  #warmSlideImages() {
    const warm = () => {
      if (!this.isConnected) return;
      this.querySelectorAll('[data-bt-hero-slide] img[loading="lazy"]').forEach((img) => {
        if (img.getClientRects().length > 0) img.setAttribute('loading', 'eager');
      });
    };
    const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(warm) : setTimeout(warm, 200));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopTimer();
    if (this.#wrapFallback != null) {
      clearTimeout(this.#wrapFallback);
      this.#wrapFallback = null;
    }
    this.#track?.removeEventListener('transitionend', this.#onTransitionEnd);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.removeEventListener('pointerenter', this.#onHoverEnter);
    this.removeEventListener('pointerleave', this.#onHoverLeave);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    this.#viewObserver?.disconnect();
    this.#viewObserver = null;
    this.#inView = false;
  }

  /** @param {PointerEvent} event */
  #onHoverEnter = (event) => {
    if (event.pointerType === 'mouse') this.#setPaused('hover', true);
  };

  /** @param {PointerEvent} event */
  #onHoverLeave = (event) => {
    if (event.pointerType === 'mouse') this.#setPaused('hover', false);
  };

  /**
   * Only keyboard focus pauses; a mouse click or tap on an arrow/dot must not hold the timer.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const target = event.target;
    this.#setPaused('focus', target instanceof Element && target.matches(':focus-visible'));
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (!(next instanceof Node) || !this.contains(next)) this.#setPaused('focus', false);
  };

  /**
   * @param {Event} event
   */
  #handleClick(event) {
    if (this.#didSwipe) {
      this.#didSwipe = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;

    const prev = target.closest('[data-bt-hero-prev]');
    if (prev) {
      event.preventDefault();
      this.#goBy(-1, true);
      return;
    }

    const next = target.closest('[data-bt-hero-next]');
    if (next) {
      event.preventDefault();
      this.#goBy(1, true);
      return;
    }

    const dot = target.closest('[data-bt-hero-dot]');
    if (dot instanceof HTMLElement && dot.dataset.index != null) {
      event.preventDefault();
      this.#goTo(Number.parseInt(dot.dataset.index, 10), true);
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

    const last = this.#slides.length - 1;
    if (event.key === 'ArrowLeft') this.#goBy(-1, true);
    else if (event.key === 'ArrowRight') this.#goBy(1, true);
    else if (event.key === 'Home') this.#goTo(0, true);
    else if (event.key === 'End') this.#goTo(last, true);
    else return;

    event.preventDefault();
    const target = event.target;
    if (target instanceof Element && target.closest('[data-bt-hero-dot]')) {
      this.#dots[this.#index]?.focus();
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown(event) {
    this.#didSwipe = false;
    if (event.pointerType === 'mouse' || this.#pointerId != null) return;
    this.#pointerId = event.pointerId;
    this.#pointerStartX = event.clientX;
    this.#pointerLastX = event.clientX;
    this.#setPaused('touch', true);
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove(event) {
    if (event.pointerId === this.#pointerId) this.#pointerLastX = event.clientX;
  }

  /**
   * pointercancel can report clientX 0, so the delta uses the last pointermove position.
   * @param {PointerEvent} event
   */
  #handlePointerUp(event) {
    if (event.pointerId !== this.#pointerId) return;
    if (event.type === 'pointerup') this.#pointerLastX = event.clientX;

    const delta = this.#pointerLastX - this.#pointerStartX;
    this.#pointerId = null;

    if (Math.abs(delta) >= SWIPE_THRESHOLD) {
      this.#didSwipe = event.type === 'pointerup';
      this.#goBy(delta < 0 ? 1 : -1, true);
    }
    this.#setPaused('touch', false);
  }

  #handleVisibility() {
    this.#setPaused('hidden', document.hidden);
  }

  /**
   * @param {TransitionEvent} event
   */
  #handleTransitionEnd(event) {
    if (event.target !== this.#track || event.propertyName !== 'transform') return;
    this.#finishWrap();
  }

  /**
   * @param {number} delta
   * @param {boolean} manual
   */
  #goBy(delta, manual) {
    const count = this.#slides.length;
    if (count < 2) return;
    const next = (this.#index + delta + count) % count;
    this.#animateTo(next, delta > 0 ? 1 : -1, manual);
  }

  /**
   * @param {number} index
   * @param {boolean} manual
   */
  #goTo(index, manual) {
    const count = this.#slides.length;
    if (!Number.isFinite(index) || index < 0 || index >= count || index === this.#index) return;

    let direction = index > this.#index ? 1 : -1;
    if (this.#index === count - 1 && index === 0) direction = 1;
    if (this.#index === 0 && index === count - 1) direction = -1;
    this.#animateTo(index, direction, manual);
  }

  /**
   * @param {number} nextIndex
   * @param {1 | -1} direction
   * @param {boolean} manual
   */
  #animateTo(nextIndex, direction, manual) {
    if (this.#animating || !this.#track) return;
    const count = this.#slides.length;
    const wrapsForward = direction === 1 && this.#index === count - 1 && nextIndex === 0;
    const wrapsBack = direction === -1 && this.#index === 0 && nextIndex === count - 1;

    this.#animating = true;
    this.#index = nextIndex;

    if (this.#reducedMotion) {
      this.#setOffset(this.#index);
      this.#animating = false;
      this.#afterChange(manual);
      return;
    }

    if (wrapsForward) {
      const clone = this.#slides[0].cloneNode(true);
      if (clone instanceof HTMLElement) {
        this.#prepareClone(clone);
        this.#track.append(clone);
        this.#track.offsetHeight;
        this.classList.add('is-animating');
        this.#setOffset(count);
        this.#armWrapFallback();
      }
    } else if (wrapsBack) {
      const clone = this.#slides[count - 1].cloneNode(true);
      if (clone instanceof HTMLElement) {
        this.#prepareClone(clone);
        this.#track.prepend(clone);
        this.#setOffset(1);
        this.#track.offsetHeight;
        this.classList.add('is-animating');
        this.#setOffset(0);
        this.#armWrapFallback();
      }
    } else {
      this.classList.add('is-animating');
      this.#setOffset(this.#index);
      this.#armWrapFallback();
    }

    this.#afterChange(manual);
  }

  #armWrapFallback() {
    if (this.#wrapFallback != null) clearTimeout(this.#wrapFallback);
    this.#wrapFallback = setTimeout(() => this.#finishWrap(), 700);
  }

  #finishWrap() {
    if (this.#wrapFallback != null) {
      clearTimeout(this.#wrapFallback);
      this.#wrapFallback = null;
    }
    if (!this.#track) {
      this.#animating = false;
      return;
    }

    this.classList.remove('is-animating');
    this.#track.querySelectorAll('[data-bt-hero-clone]').forEach((clone) => clone.remove());
    this.#setOffset(this.#index);
    this.#animating = false;
  }

  /**
   * @param {HTMLElement} clone
   */
  #prepareClone(clone) {
    clone.setAttribute('data-bt-hero-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.style.pointerEvents = 'none';
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    clone.querySelectorAll('video').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
    });
  }

  /**
   * @param {number} index
   */
  #setOffset(index) {
    if (!this.#track) return;
    this.#track.style.transform = `translate3d(${-index * 100}%, 0, 0)`;
  }

  /**
   * @param {boolean} manual
   */
  #afterChange(manual) {
    const active = document.activeElement;
    const focusWasInSlide = active instanceof HTMLElement && !!this.#track?.contains(active);

    this.#updateChrome();
    this.#syncVideos();
    this.#restartProgress();

    if (this.#status) {
      this.#status.setAttribute('aria-live', manual ? 'polite' : 'off');
      this.#status.textContent = SLIDE_STATUS.replace('{{ index }}', String(this.#index + 1)).replace(
        '{{ count }}',
        String(this.#slides.length)
      );
    }

    if (manual) {
      if (focusWasInSlide) {
        const link = this.#visibleFocusable(this.#slides[this.#index]);
        (link ?? this).focus({ preventScroll: true });
      }
      this.#syncTimer(true);
    }
  }

  /**
   * Slides render desktop + mobile copy; only one is displayed per breakpoint.
   * @param {HTMLElement | undefined} slide
   * @returns {HTMLElement | null}
   */
  #visibleFocusable(slide) {
    if (!slide) return null;
    const nodes = slide.querySelectorAll('a[href], button:not([disabled])');
    for (const node of nodes) {
      if (node instanceof HTMLElement && node.getClientRects().length > 0) return node;
    }
    return null;
  }

  #updateChrome() {
    this.#slides.forEach((slide, index) => {
      const active = index === this.#index;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('inert', !active);
    });

    this.#dots.forEach((dot, index) => {
      const active = index === this.#index;
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
      dot.tabIndex = active ? 0 : -1;
    });
  }

  #syncVideos() {
    this.#slides.forEach((slide, index) => {
      slide.querySelectorAll('video').forEach((video) => {
        if (index === this.#index && !this.#reducedMotion) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  #restartProgress() {
    const fill = this.#progressFill;
    if (!fill) return;
    const parent = fill.parentElement;
    if (!parent) return;
    const next = fill.cloneNode(true);
    if (!(next instanceof HTMLElement)) return;
    fill.replaceWith(next);
    this.#progressFill = next;
  }

  /**
   * @param {string} reason
   * @param {boolean} paused
   */
  #setPaused(reason, paused) {
    if (paused) this.#pauseReasons.add(reason);
    else this.#pauseReasons.delete(reason);
    this.#syncTimer();
  }

  /**
   * @param {boolean} [restart] - Start a fresh interval even if one is running.
   */
  #syncTimer(restart = false) {
    const shouldRun = this.#autoplaySeconds > 0 && !this.#reducedMotion && this.#pauseReasons.size === 0;
    this.toggleAttribute('data-paused', !shouldRun);
    if (!shouldRun) this.#stopTimer();
    else if (restart || this.#timer == null) this.#startTimer();
  }

  #startTimer() {
    this.#stopTimer();
    this.#restartProgress();
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#goBy(1, false);
      this.#syncTimer();
    }, this.#autoplaySeconds * 1000);
  }

  #stopTimer() {
    if (this.#timer == null) return;
    clearTimeout(this.#timer);
    this.#timer = null;
  }
}

if (!customElements.get('bt-hero-carousel')) {
  customElements.define('bt-hero-carousel', BtHeroCarousel);
}
